import { useState, useEffect, useMemo, useRef } from 'react';
import { devError, devWarn } from '../../utils/devLog';

const WRITE_DEBOUNCE_MS = 500;

const useLocalStorage = (key, initialValue) => {
  const [value, setValue] = useState(() => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : initialValue;
    } catch (error) {
      devError(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        if (error && error.name === 'QuotaExceededError') {
          devWarn(
            `localStorage quota exceeded while writing "${key}"; skipping.`,
          );
          return;
        }
        devError(`Error writing to localStorage key "${key}":`, error);
      }
    }, WRITE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [key, value]);

  return useMemo(() => [value, setValue], [value]);
};

export default useLocalStorage;
